import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { lojaApi } from '../../services/api';
import { LogoMark } from '../../components/ui';
import { formatarMoeda } from '../../lib/format';
import { useCarrinho } from './carrinho';
import {
  ArrowLeft, QrCode, CreditCard, ShieldCheck, ShoppingBag, User, Phone, Mail,
} from 'lucide-react';

export function CheckoutPage() {
  const { itens, total, limpar } = useCarrinho();
  const navigate = useNavigate();

  // Pré-preenche com os dados da conta do cliente, se estiver logado
  const clienteCache = (() => {
    try {
      const raw = localStorage.getItem('monchery_cliente');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [nome, setNome] = useState(clienteCache?.nome || '');
  const [telefone, setTelefone] = useState(clienteCache?.telefone || '');
  const [email, setEmail] = useState(clienteCache?.email || '');
  const [observacoes, setObservacoes] = useState('');
  const [metodo, setMetodo] = useState<'PIX' | 'CARTAO'>('PIX');
  const [erro, setErro] = useState('');

  const criarPedido = useMutation({
    mutationFn: (data: any) => lojaApi.criarPedido(data),
    onSuccess: (res) => {
      const { pedido, checkout } = res.data;
      limpar();
      // Se o Mercado Pago estiver configurado, redireciona para o checkout
      if (checkout?.initPoint) {
        window.location.href = checkout.initPoint;
        return;
      }
      navigate(`/loja/pedido/${pedido.id}`);
    },
    onError: (err: any) => {
      setErro(err.response?.data?.error || err.message || 'Não foi possível criar o pedido.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    if (itens.length === 0) {
      setErro('Seu carrinho está vazio.');
      return;
    }
    criarPedido.mutate({
      clienteNome: nome.trim(),
      clienteTelefone: telefone.replace(/\D/g, ''),
      clienteEmail: email.trim() || undefined,
      observacoes: observacoes.trim() || undefined,
      pagamentoMetodo: metodo,
      itens: itens.map(i => ({
        produtoId: i.produtoId,
        tamanho: i.tamanho,
        cor: i.cor,
        estampaId: i.estampaId || null,
        quantidade: i.quantidade,
      })),
    });
  };

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Header */}
      <header className="bg-white border-b border-ink-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/loja" className="flex items-center gap-2.5">
            <LogoMark size={30} />
            <span className="font-display font-bold text-ink-950 tracking-tight">MONCHERY</span>
          </Link>
          <span className="flex items-center gap-1.5 text-2xs font-semibold text-ink-400">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            Compra segura
          </span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/loja"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-500 hover:text-ink-800 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Continuar comprando
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Formulário */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 card space-y-5">
            <h1 className="font-display text-xl font-bold text-ink-950 tracking-tight">
              Finalizar pedido
            </h1>

            <div>
              <label className="input-label" htmlFor="ck-nome">Nome completo *</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                <input
                  id="ck-nome"
                  type="text"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  placeholder="Seu nome"
                  className="input-field !pl-10"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label" htmlFor="ck-tel">Telefone / WhatsApp *</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="ck-tel"
                    type="tel"
                    value={telefone}
                    onChange={e => setTelefone(e.target.value)}
                    placeholder="(44) 99999-9999"
                    className="input-field !pl-10"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="input-label" htmlFor="ck-email">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="ck-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Opcional"
                    className="input-field !pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Pagamento */}
            <div>
              <p className="input-label">Forma de pagamento</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMetodo('PIX')}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    metodo === 'PIX'
                      ? 'border-brand-600 bg-brand-50/60'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <QrCode className={`w-6 h-6 ${metodo === 'PIX' ? 'text-brand-600' : 'text-ink-400'}`} />
                  <span>
                    <span className="block text-sm font-bold text-ink-900">PIX</span>
                    <span className="block text-xs text-ink-400">Aprovação imediata</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setMetodo('CARTAO')}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    metodo === 'CARTAO'
                      ? 'border-brand-600 bg-brand-50/60'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <CreditCard className={`w-6 h-6 ${metodo === 'CARTAO' ? 'text-brand-600' : 'text-ink-400'}`} />
                  <span>
                    <span className="block text-sm font-bold text-ink-900">Cartão</span>
                    <span className="block text-xs text-ink-400">Crédito em até 12x</span>
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label className="input-label" htmlFor="ck-obs">Observações</label>
              <textarea
                id="ck-obs"
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                rows={3}
                placeholder="Ex: estampa nas costas, entrega após as 18h..."
                className="input-field"
              />
            </div>

            {erro && (
              <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700 animate-fade-in">
                {erro}
              </div>
            )}

            <button
              type="submit"
              disabled={criarPedido.isPending || itens.length === 0}
              className="btn-primary w-full !py-3.5 !text-[15px]"
            >
              {criarPedido.isPending
                ? 'Processando...'
                : `Pagar ${formatarMoeda(total)} ${metodo === 'PIX' ? 'no PIX' : 'no cartão'}`}
            </button>
            <p className="text-center text-2xs text-ink-400 flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              Pagamento processado com segurança pelo Mercado Pago.
            </p>
          </form>

          {/* Resumo */}
          <aside className="lg:col-span-2 card lg:sticky lg:top-6">
            <h2 className="card-title flex items-center gap-2 mb-4">
              <ShoppingBag className="w-4 h-4 text-brand-600" />
              Resumo do pedido
            </h2>
            {itens.length === 0 ? (
              <p className="text-sm text-ink-400 py-4 text-center">Seu carrinho está vazio.</p>
            ) : (
              <>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {itens.map(item => (
                    <div key={item.chave} className="flex gap-3">
                      <div className="w-12 h-12 rounded-lg bg-ink-100 overflow-hidden shrink-0 flex items-center justify-center">
                        {item.estampaImagem ? (
                          <img src={item.estampaImagem} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span
                            className="w-6 h-6 rounded-full ring-1 ring-inset ring-black/10"
                            style={{ backgroundColor: item.corHex || '#999' }}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-ink-800 truncate">
                          {item.quantidade}× {item.produtoNome}
                        </p>
                        <p className="text-xs text-ink-400">
                          {item.cor} · {item.tamanho}
                          {item.estampaNome ? ` · ${item.estampaNome}` : ' · Lisa'}
                        </p>
                      </div>
                      <p className="text-[13px] font-bold text-ink-900 whitespace-nowrap">
                        {formatarMoeda(item.valorUnitario * item.quantidade)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-ink-100 mt-4 pt-4 space-y-1.5">
                  <div className="flex justify-between text-sm text-ink-500">
                    <span>Subtotal</span>
                    <span>{formatarMoeda(total)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-ink-500">
                    <span>Entrega/retirada</span>
                    <span className="text-ink-400">a combinar</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-ink-100">
                    <span className="font-semibold text-ink-900">Total</span>
                    <span className="font-display text-lg font-bold text-ink-950">
                      {formatarMoeda(total)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
